import { KNOWLEDGE, STOPWORDS, SYNONYMS, tokenise, type Answer } from './knowledge';

/**
 * Finding the right answer, with no model behind it.
 *
 * Scoring rather than intelligence. A query is reduced to meaningful words,
 * each is expanded through a small synonym map, and every entry is scored on
 * how many of those words it carries and where. The result is deterministic,
 * instant, offline, and — importantly — knowably wrong in ways that can be
 * fixed by editing a keyword list.
 *
 * The floor matters more than the ceiling. Answering "how do I delete a lead"
 * with a page about invoices because both mention "the" is worse than saying
 * nothing, so a weak best match is treated as no match at all.
 */

export interface Match {
  answer: Answer;
  score: number;
}

/**
 * Below this, the best candidate is not good enough to show as an answer.
 *
 * Set just under the value of one exact keyword hit on a rare word, so a
 * one-word question like "permissions" is answerable, while a fuzzy keyword
 * hit — half that — still is not enough on its own to speak up.
 */
const FLOOR = 1.0;

/** And below this share of the best score, a runner-up is not worth showing. */
const RELATIVE = 0.75;

/**
 * One edit apart, for words of four letters or more.
 *
 * Cheap on purpose: full Levenshtein over every keyword on every keystroke is
 * work nobody sees the benefit of. This catches "invoce" and "delet", which
 * is the overwhelming majority of real typing.
 */
function nearlyEqual(a: string, b: string): boolean {
  if (a === b) return true;
  if (a.length < 4 || b.length < 4) return false;
  if (Math.abs(a.length - b.length) > 1) return false;

  // Same length: allow one substitution.
  if (a.length === b.length) {
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i] && (diff += 1) > 1) return false;
    }
    return diff === 1;
  }

  // Differing by one: allow one insertion or deletion.
  const [short, long] = a.length < b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < short.length && j < long.length) {
    if (short[i] === long[j]) {
      i += 1;
      j += 1;
      continue;
    }
    if (skipped) return false;
    skipped = true;
    j += 1;
  }
  return true;
}

/**
 * "delete", "deleting", "deleted" and "deletes" are one word here.
 *
 * Four suffixes, and no more. A real stemmer turns "business" into "busine"
 * and "access" into "acces", which costs more matches than it wins; this
 * handles the endings that actually differ between how a question is asked
 * ("how do I delete a lead") and how a heading is written ("Deleting several
 * records at once"), which was returning nothing useful for exactly that.
 *
 * The length guards are the whole safety margin: they keep "used", "need",
 * "thing" and "less" intact. Applied to both sides of every comparison, so
 * an over-eager trim costs nothing as long as it is consistent.
 */
function stem(word: string): string {
  let w = word;
  if (w.length > 5 && w.endsWith('ing')) w = w.slice(0, -3);
  else if (w.length > 5 && w.endsWith('ed')) w = w.slice(0, -2);
  else if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) w = w.slice(0, -1);
  if (w.length > 4 && w.endsWith('e')) w = w.slice(0, -1);
  return w;
}

/** Query words, minus noise, stemmed, plus whatever the synonyms add. */
export function expand(query: string): string[] {
  const words = tokenise(query).filter((w) => !STOPWORDS.has(w));
  const out = new Set<string>();

  for (const w of words) {
    const base = stem(w);
    out.add(base);
    // Looked up under both spellings, so "bills" finds the entry filed
    // under "bill" without every plural needing its own map entry.
    for (const syn of [...(SYNONYMS[w] ?? []), ...(SYNONYMS[base] ?? [])]) out.add(stem(syn));
  }

  return [...out];
}

/**
 * How much each word is worth, from how rare it is in the index.
 *
 * Without this every word counted the same, so "export my leads" scored
 * Exporting records exactly level with Adding a lead — both matched one title
 * word — and the tie was broken by declaration order. But "lead" is in eight
 * entries and "export" is in two, and the rare word is the one carrying the
 * question. Weighting by rarity is what separates them, and it needs no
 * hand-tuning: it falls out of the index and updates itself when entries are
 * added.
 */
const DOC_FREQ = (() => {
  const freq = new Map<string, number>();
  for (const a of KNOWLEDGE) {
    const seen = new Set(
      [...tokenise(a.title), ...a.keywords.map((k) => k.toLowerCase())].map(stem),
    );
    for (const w of seen) freq.set(w, (freq.get(w) ?? 0) + 1);
  }
  return freq;
})();

function idf(word: string): number {
  return Math.log(1 + KNOWLEDGE.length / (1 + (DOC_FREQ.get(word) ?? 0)));
}

/** The mean, so weights hover around 1 and the floor keeps its meaning. */
const AVG_IDF =
  [...DOC_FREQ.keys()].reduce((sum, w) => sum + idf(w), 0) / Math.max(1, DOC_FREQ.size);

function weight(word: string): number {
  return idf(word) / AVG_IDF;
}

/**
 * Words the index has never heard of carry no information, so they should not
 * count against the words that do.
 *
 * "What am I allowed to do" was scoring nothing at all: one word matched, five
 * were divided into it, and the result fell under the floor. Dropping the
 * unknown ones first leaves the question that was actually asked. Gibberish
 * reduces to nothing under the same rule, which is exactly the behaviour we
 * want at the other end.
 */
function known(word: string): boolean {
  if (DOC_FREQ.has(word)) return true;
  for (const w of DOC_FREQ.keys()) if (nearlyEqual(w, word)) return true;
  return false;
}

function scoreOne(answer: Answer, words: string[]): number {
  const title = new Set(tokenise(answer.title).map(stem));
  const keys = new Set(answer.keywords.map((k) => stem(k.toLowerCase())));

  let score = 0;
  let titleHits = 0;

  for (const w of words) {
    /*
     * The best of the four, not the first of the four.
     *
     * Checking in order and stopping meant a word that was an exact keyword
     * and a near-miss on the title scored 1 rather than 1.5, which put
     * "reminder before due" under the floor and returned nothing for a
     * question the index could answer perfectly well.
     */
    let best = 0;

    // A word in the title is the strongest signal there is: somebody typing
    // "drive" wants the entry called Drive, not the six that mention it.
    if (title.has(w)) {
      best = 2.5;
      titleHits += 1;
    } else if ([...title].some((t) => nearlyEqual(t, w))) best = 1.5;

    if (best < 1 && keys.has(w)) best = 1;
    else if (best < 0.5 && [...keys].some((k) => nearlyEqual(k, w))) best = 0.5;

    score += best * weight(w);
  }

  /*
   * Divide by a gentle function of the query length, not by the length itself.
   * A long question should not be penalised into nothing for containing words
   * no entry happens to carry, but a query that matched two words out of three
   * should still rank above one that matched two out of nine.
   */
  const base = score / Math.sqrt(Math.max(1, words.length));

  /*
   * And a nudge for how much of the heading the question accounted for.
   *
   * "Open a spreadsheet" tied Writing documents and spreadsheets against
   * Importing leads from a spreadsheet at the same score — one title word
   * each. The first title is *about* spreadsheets; the second mentions them
   * in passing. Coverage is what tells those apart, and it is deliberately a
   * small term: a tiebreak, not a second ranking.
   */
  const coverage = titleHits / Math.max(1, title.size);
  return base * (1 + 0.2 * coverage);
}

/**
 * The best few answers for a question.
 *
 * `granted` filters out entries for screens this person cannot reach — an
 * answer pointing at a page that is not in their sidebar is worse than no
 * answer, because it reads as the product being broken.
 */
export function search(query: string, granted: (feature: string) => boolean, limit = 3): Match[] {
  const words = expand(query).filter(known);
  if (words.length === 0) return [];

  const ranked = KNOWLEDGE.filter((a) => !a.feature || granted(a.feature))
    .map((answer) => ({ answer, score: scoreOne(answer, words) }))
    .filter((m) => m.score >= FLOOR)
    .sort((a, b) => b.score - a.score);

  const top = ranked[0];
  if (!top) return [];

  /*
   * Only what is in the same league as the best.
   *
   * Padding a confident answer out to three put a lead-import card under
   * "where do I upload files" — it shared one word and nothing else. Somebody
   * reading three cards assumes all three are relevant, so a distant third is
   * not a bonus, it is a wrong answer with a right one above it.
   */
  const cutoff = top.score * RELATIVE;
  return ranked.filter((m) => m.score >= cutoff).slice(0, limit);
}

/** Look one up by id, for the follow-up chips. */
export function byId(id: string): Answer | undefined {
  return KNOWLEDGE.find((a) => a.id === id);
}
